import { IServerSideGetRowsParams } from 'ag-grid-community'
import { AppThunkDispatch, useAppSelector } from 'appState/store'
import NoResultsAfterFilters from 'assets/images/table/NoResultsAfterFilters.svg?react'
import { apiPaths } from 'configs/apiConfigs'
import { resetCommonClaimsFilters } from 'features/claims/claimsSlice'
import { ClaimStatusForRouteType } from 'features/claims/types'
import { settingsServiceApiPaths, setUserPreference } from 'features/settings/settingsApi'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import styled, { css } from 'styled-components'
import { httpClaims } from 'utils/http'

import { SUPPORTED_OPERATION, USER_COLUMN_STATE } from './ClaimsTableConstants'

export function getSelectedClaimStatus() {
  const claimStatusFromRoute = location.pathname.split('/')[2] as ClaimStatusForRouteType
  return claimStatusFromRoute
}

export function getSelectedBatchIdFromUrl() {
  const batchId = location.pathname.split('/')[5]
  return batchId
}

export async function setSupportedOperationsInLocalStorage() {
  await httpClaims.get(apiPaths.supportedOperations()).then((response) => {
    const data = response.data
    localStorage.setItem(SUPPORTED_OPERATION, JSON.stringify(data))
  })
}

export const clearAllFilters = async (
  params: IServerSideGetRowsParams,
  dispatch: AppThunkDispatch,
  filterLength: number,
) => {
  if (params) {
    !!filterLength && dispatch(resetCommonClaimsFilters())
    await setUserPreference(settingsServiceApiPaths.tenantUserPreferencesPut(), {
      key: UserPreferencesKeysEnum.claimsGridCommonFilters,
      value: JSON.stringify({}),
    })
    params.api.setFilterModel(null)
  } else {
    console.log('obGridParams undefined')
  }
}

export const getNoRowDataMessage = () => {
  const noRowDataSet = {
    title: 'Your filters produced no results.',
    message: 'Try adjusting or removing filters to find what you’re looking for.',
    image: <NoResultsAfterFilters />,
  }

  const noRowDataMessage = noRowDataSet

  return (
    <ClearFilterDiv data-testid="no-content-container">
      {noRowDataMessage.image}
      <div className="details">
        {noRowDataMessage.title && (
          <p data-testid="title" className="title">
            {noRowDataMessage.title}
          </p>
        )}
        {noRowDataMessage.message && (
          <p data-testid="message" className="message">
            {noRowDataMessage.message}
          </p>
        )}
      </div>
    </ClearFilterDiv>
  )
}

export function isSelectedClaimIsOwnedByCurrentUser(totalClaims: any, ownerId: string) {
  return totalClaims.filter((claim: any) => claim?.ownerId !== ownerId)
}

export function isSelectedClaimsHasOnlyNonOwners(totalClaims: any[], ownerId: string): boolean {
  return totalClaims.every((claim: any) => {
    if (claim?.owner && claim?.ownerId !== ownerId) {
      return false
    } else {
      return true
    }
  })
}

export function getAllTabsByCurrentRoute() {
  const isBatchIdAvailableOnRoute = location.pathname.split('/')[5]

  const { claimStatus, batchId } = useAppSelector((state) => state.claims.allClaimRouteUrl)

  const needsWorkPath = isBatchIdAvailableOnRoute
    ? `${claimStatus}/needs-work/batch/${batchId}`
    : `${claimStatus}/needs-work`
  const onLiveCallPath = isBatchIdAvailableOnRoute
    ? `${claimStatus}/on-live-call/batch/${batchId}`
    : `${claimStatus}/on-live-call`
  const queuedPath = isBatchIdAvailableOnRoute ? `${claimStatus}/queued/batch/${batchId}` : `${claimStatus}/queued`
  const archivedPath = isBatchIdAvailableOnRoute
    ? `${claimStatus}/archived/batch/${batchId}`
    : `${claimStatus}/archived`

  return {
    needsWorkPath,
    onLiveCallPath,
    queuedPath,
    archivedPath,
  }
}

const ClearFilterDiv = styled.div<{ maxWidth?: string }>`
  position: absolute !important;
  inset: 0 0 0 0;
  height: 100%;
  width: 100%;
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 1.25em;
  position: relative;
  top: -1em;

  svg{
    width:243px;
    height:221px;
  }

  .details {
    ${({ maxWidth }) =>
    maxWidth &&
    css`
        max-width: ${maxWidth};
      `}
    white-space: pre-wrap;

    > .title {
      color: ${({ theme }) => theme.colors.primary.darker};
      ${({ theme }) => theme.typography.h5.bold};
      margin-top: 1.25;
    }

    > .message {
      ${({ theme }) => theme.typography.m.normal};
      color: ${({ theme }) => theme.colors.grey.darker};
    }
  }
`

export const storeColumPreferenceInLocalStorage = (gridApi: any) => {
  const columnState = gridApi.api.getColumnState()

  if (columnState.length > 0) {
    const columnLocalStateWithoutSelection = columnState.filter((obj: any) => {
      if (obj.colId !== 'select') {
        return obj
      }
    })

    localStorage.setItem(USER_COLUMN_STATE, JSON.stringify(columnLocalStateWithoutSelection))
  } else {
    localStorage.setItem(USER_COLUMN_STATE, JSON.stringify(columnState))
  }
}
